import { useState, useEffect, useRef } from 'react';
import { Text, Button } from '@qwickapps/react-framework';
import { config } from '../../config/runtime';

interface PullRequest {
  id: string;
  number: number;
  title: string;
  repo: string;
  author: string;
  status: 'open' | 'merged' | 'closed';
  reviewStatus?: 'pending' | 'approved' | 'changes_requested';
  createdAt: string;
  htmlUrl?: string;
}

interface PROverviewProps {
  pullRequests?: PullRequest[];
  loading?: boolean;
  onReview?: (pr: PullRequest) => void;
  onMerge?: (pr: PullRequest) => void;
}

const GITHUB_REPOS = ['raajkumars/qwickapps', 'qwickapps/qwickresume'];

async function fetchGitHubPRs(): Promise<PullRequest[]> {
  const results: PullRequest[] = [];

  for (const repo of GITHUB_REPOS) {
    try {
      const res = await fetch(
        `/api/github/pulls?repo=${encodeURIComponent(repo)}&state=open&per_page=20`,
      );
      if (!res.ok) continue;
      const data = await res.json() as { configured: boolean; pull_requests?: Array<{
        id: number;
        number: number;
        title: string;
        html_url: string;
        state: string;
        user: { login: string };
        created_at: string;
        draft: boolean;
      }> };
      if (!data.pull_requests) continue;

      const repoName = repo.split('/')[1];
      for (const pr of data.pull_requests) {
        results.push({
          id: String(pr.number),
          number: pr.number,
          title: pr.title,
          repo: repoName,
          author: pr.user?.login ?? 'unknown',
          status: 'open',
          reviewStatus: undefined,
          createdAt: pr.created_at,
          htmlUrl: pr.html_url,
        });
      }
    } catch {
      // Skip repo on error
    }
  }

  return results;
}

function daysAgo(dateStr: string): number {
  const created = new Date(dateStr).getTime();
  const now = Date.now();
  return Math.floor((now - created) / (1000 * 60 * 60 * 24));
}

export default function PROverview({
  pullRequests: pullRequestsProp,
  loading: loadingProp = false,
  onReview,
  onMerge,
}: PROverviewProps) {
  const isSelfManaged = pullRequestsProp === undefined;
  const [pullRequests, setPullRequests] = useState<PullRequest[]>(pullRequestsProp ?? []);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!isSelfManaged || hasFetched.current) return;
    hasFetched.current = true;

    fetchGitHubPRs()
      .then(prs => setPullRequests(prs))
      .catch(() => {/* stay empty */});
  }, [isSelfManaged]);

  useEffect(() => {
    if (!isSelfManaged && pullRequestsProp !== undefined) {
      setPullRequests(pullRequestsProp);
    }
  }, [pullRequestsProp, isSelfManaged]);

  if (loadingProp) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
        <div role="progressbar" aria-label="loading" style={{
          width: 24,
          height: 24,
          border: '2px solid var(--theme-border-color)',
          borderTopColor: 'var(--theme-color-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  const openPRs = pullRequests.filter(pr => pr.status === 'open');
  const mergedPRs = pullRequests.filter(pr => pr.status === 'merged');
  const closedPRs = pullRequests.filter(pr => pr.status === 'closed');

  return (
    <div>
      <Text variant="h5" content="Code Review" gutterBottom />
      <Text
        variant="body2"
        content="Review and manage pull requests"
        customColor="var(--theme-text-secondary)"
        gutterBottom
      />

      <div style={{ display: 'flex', gap: 32, marginTop: 16 }}>
        <StatBox label="Open" value={openPRs.length} color="#2196f3" />
        <StatBox label="Merged" value={mergedPRs.length} color="#4caf50" />
        <StatBox label="Closed" value={closedPRs.length} color="#9e9e9e" />
      </div>

      {pullRequests.length === 0 ? (
        <div
          style={{
            marginTop: 24,
            padding: 32,
            borderRadius: 8,
            background: 'var(--theme-bg-surface)',
            border: '1px solid var(--theme-border-color)',
            textAlign: 'center',
          }}
        >
          <Text
            variant="body1"
            content="No pull requests found"
            customColor="var(--theme-text-secondary)"
          />
        </div>
      ) : (
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {openPRs.length === 0 && (
            <div style={{ marginTop: 16, padding: 16, borderRadius: 4, background: '#e8f5e9', border: '1px solid #a5d6a7' }}>
              <Text variant="body2" content="No open pull requests — all clear!" customColor="#2e7d32" />
            </div>
          )}
          {pullRequests.map(pr => (
            <PRCard
              key={pr.id}
              pr={pr}
              onReview={() => onReview?.(pr)}
              onMerge={() => onMerge?.(pr)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PRCard({
  pr,
  onReview,
  onMerge,
}: {
  pr: PullRequest;
  onReview?: () => void;
  onMerge?: () => void;
}) {
  const statusColors = {
    open: '#2196f3',
    merged: '#4caf50',
    closed: '#9e9e9e',
  };

  const reviewColors = {
    pending: '#ff9800',
    approved: '#4caf50',
    changes_requested: '#f44336',
  };

  const age = daysAgo(pr.createdAt);

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 4,
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: statusColors[pr.status],
            flexShrink: 0,
          }}
        />
        {pr.htmlUrl ? (
          <a
            href={pr.htmlUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Text variant="subtitle2" content={`#${pr.number}`} />
          </a>
        ) : (
          <Text variant="subtitle2" content={`#${pr.number}`} />
        )}
        <Text variant="body1" content={pr.title} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, paddingLeft: 24 }}>
        <Text variant="caption" content={pr.repo} customColor="var(--theme-text-secondary)" />
        <Text variant="caption" content={`by ${pr.author}`} customColor="var(--theme-text-disabled)" />
        <Text variant="caption" content={`${age}d ago`} customColor="var(--theme-text-disabled)" />

        {pr.reviewStatus && (
          <div
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              background: reviewColors[pr.reviewStatus],
              color: 'white',
              fontSize: 10,
            }}
          >
            {pr.reviewStatus}
          </div>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {pr.status === 'open' && (
            <>
              <Button size="small" onClick={onReview}>Review</Button>
              <Button size="small" variant="primary" onClick={onMerge}>Merge</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface StatBoxProps {
  label: string;
  value: number;
  color: string;
}

function StatBox({ label, value, color }: StatBoxProps) {
  return (
    <div style={{ textAlign: 'center' }}>
      <Text variant="h4" content={String(value)} customColor={color} />
      <Text variant="caption" content={label} customColor="var(--theme-text-secondary)" />
    </div>
  );
}
